/** lag-reorderable
 *
 *  Provides drag & drop (or select and move via function call) functionality
 *  for a list of 'rows'. The rows can be table TR elements or any series of elements.
 *
 *  Use the "auto-init" attribute for the component to automatically find a TABLE or UL
 *  element within, otherwise use the init call to initialize manually.
 *
 *  CSS classes are added/removed from rows to indicate state.
 *      'selected': If the row is selected, style on your rows to indicate selected state.
 *      'dragging': If the row is being dragged, style on your rows to indicate it is being dragged.
 *
 *  An attribute 'data-changed' is added to rows if they are moved/reordered.
 *  This can be used to know which rows should be updated in the database/etc.
 *
 *  Use the onSelected(handler) call to specify a function to handle when selection changes,
 *  this is useful if you want to enable/disable buttons that call the moveSelectedRowsUp/Down/etc functions.
 *  
 *  EXAMPLE USAGE:
 *      <lag-reorderable auto-init>
 *          <table>
 *              <tbody>
 *                  <tr>...</tr>
 *                  ...
 *              </tobody>
 *          </table>
 *      </lag-reorderable>
 */

import { defineElement } from '../../utility/defineElement.js';

defineElement(class extends HTMLElement {

    static get is() { return 'lag-reorderable'; }

    connectedCallback() {
        if (this.hasAttribute('auto-init')) {
            this.init();
        }
    }

    /**
     * Initialize the reorderable list functionality. Run this after the rows have been rendered into the DOM.
     * 
     * @param {Element} [rowsContainer] - Optional HTML Element object that contains the row elements (eg TBODY or UL elements). If not specified this component will automatically look for TBODY|TABLE|UL elements.
     * @param {any} fnGetRowValue - Optional function(rowElement) that must return the reorderable value from the specified element. If not specified then for TR elements the contents of the first TD will be used, else the contents of the element itself.
     * @param {any} fnSetRowValue - Optional function(rowElement, value) that must set the new/reordered value into the element AND return the OLD value. If not specified then default setting/getting logic will apply similar to the fnGetRowValue default logic.
     */
    init(rowsContainer, fnGetRowValue, fnSetRowValue) {
        // If no container provided then auto-init by trying to find a typical container of rows.
        if (rowsContainer === undefined) {
            rowsContainer = this.querySelector('table tbody') || this.querySelector('table') || this.querySelector('ul');
        }

        if (!rowsContainer || !rowsContainer.children) {
            throw new Error('ReorderableRows.init requires a container element with children.');
        }

        this._rowsContainer = rowsContainer;

        this._fnGetRowValue = fnGetRowValue;
        this._fnSetRowValue = fnSetRowValue;

        const rowsCollection = this._getRows(true);

        if (!rowsCollection.length) {
            throw new Error('ReorderableRows.init requires a container element with more than one visible children.');
        }

        // Reset cached values.
        this._isSelecting = false;
        this._draggingElement = null;
        this._draggingStartIndex = 0;

        // If the element is a TR then it will have a rowIndex (otherwise default to zero),
        // if TR then there may be rows in a thead which will would offset the tbody rowIndex values,
        // so we need to know that for when we use the rowIndex values (see getIndex helper).
        this._indexOffset = rowsCollection[0].rowIndex || 0;

        // Initialize rows as needed.
        rowsCollection.forEach((ele) => {
            ele.draggable = true;

            ele.addEventListener('dragstart', (ev) => this._handleDragStart(ev));
            ele.addEventListener('dragover', (ev) => this._handleDragOver(ev));
            ele.addEventListener('dragend', (ev) => this._handleDragEnd(ev));
            ele.addEventListener('click', (ev) => this._handleSelect(ev));
        });

        return this;
    }

    moveSelectedRowsUp() {
        this._moveSelectedRows(this._moveSelectedRows_up);
    }
    moveSelectedRowsDown() {
        this._moveSelectedRows(this._moveSelectedRows_down);
    }
    clearSelectedRows() {
        this._getRows().forEach(row => row.classList.remove('selected'));
        this._isSelecting = false;

        this._fireSelectionEvent(false);
    }
    onSelected(fnOnSelected) {
        this._fnOnSelected = fnOnSelected;
    }

    // ========================================================================

    _getRows(validate) {
        const rows = [...this._rowsContainer.children];
        return !validate ? rows
            : rows.filter(r =>
                r.nodeType === 1
                && r.style.display !== 'none'
                && r.nodeName !== 'TEMPLATE'
                && r.nodeName !== 'DOM-REPEAT'
                && r.nodeName !== 'DOM-IF'
            );
    }

    _getRowIndex(row, rowsCollection) {
        rowsCollection = rowsCollection || this._getRows();
        const rowIndex = (row.rowIndex >= 0) ? row.rowIndex : rowsCollection.indexOf(row);
        return rowIndex - this._indexOffset;
    }

    _firstRowOf(collection) { return collection[0]; }
    _lastRowOf(collection) { return collection[collection.length - 1]; }

    _getRowValue(row) {
        // If a custom GET implementation is provided use that.
        if (this._fnGetRowValue) {
            return this._fnGetRowValue(row);
        }
        // else use default implementation...

        row = this._getRowValueElement(row);

        return row.innerHTML;
    }
    _setRowValue(row, val) {
        // If a custom SET implementation is provided use that.
        if (this._fnSetRowValue) {
            this._fnSetRowValue(row, val);
            return;
        }
        // else use default implementation...

        row = this._getRowValueElement(row);

        const v = row.innerHTML;
        row.innerHTML = val;

        return v;
    }
    _getRowValueElement(row) {
        // If row is a TR then swap the first TD contents,
        // otherwise just swap the contents of the element.
        if (row.nodeName === 'TR' && row.children.length > 0) {
            row = row.children[0];
        }

        return row;
    }

    _handleSelect(ev) {
        ev.currentTarget.classList.toggle('selected');

        this._isSelecting = !!this._getRows().find(row => row.classList.contains('selected')); // Any rows are selected.

        this._fireSelectionEvent(this._isSelecting);
    }
    _fireSelectionEvent(rowsAreSelected) {
        this._fnOnSelected && this._fnOnSelected(rowsAreSelected);
    }

    _handleDragStart(ev) {
        window.getSelection().removeAllRanges(); // Selection can cause glitchy UI, so unselect.

        // Don't allow dragging when rows are selected, must use move functions instead.
        if (this._isSelecting) {
            ev.preventDefault();
            return;
        }

        ev.dataTransfer.effectAllowed = 'move';

        this._draggingElement = ev.currentTarget;
        this._draggingElement.classList.toggle('dragging', true);
        this._draggingStartIndex = this._getRowIndex(this._draggingElement);
    }
    _handleDragOver(ev) {
        if (!this._draggingElement) { return; }

        ev.preventDefault();
        ev.dataTransfer.dropEffect = 'move';

        const eleDragOver = ev.currentTarget;

        // Swap the row positions...

        const rowsCollection = this._getRows();
        const idxDragOver = this._getRowIndex(eleDragOver, rowsCollection);
        const idxDragging = this._getRowIndex(this._draggingElement, rowsCollection);

        if (idxDragOver > idxDragging) {
            eleDragOver.after(this._draggingElement);
        }
        else {
            eleDragOver.before(this._draggingElement);
        }
    }
    _handleDragEnd(ev) {
        // If move is successful (not cancelled/'none') then perform move over the effected range.
        if (ev.dataTransfer.dropEffect === 'move') {
            const rowsCollection = this._getRows(true);
            const endIndex = this._getRowIndex(this._draggingElement, rowsCollection);

            this._resequenceRowRangeValues(this._draggingStartIndex, endIndex, rowsCollection);
        }
        else {
            // Put the dragging element back where it was since user cancelled the drag.
            // If it was at the start (0) then put it there, else put it at the correct index.
            if (this._draggingStartIndex === 0) {
                this._rowsContainer.prepend(this._draggingElement);
            }
            else {
                this._getRows(true)[this._draggingStartIndex - 1].after(this._draggingElement);
            }
        }

        this._draggingElement.classList.toggle('dragging', false);
        this._draggingElement = null;
    }

    _resequenceRowRangeValues(movedFromIndex, movedToIndex, rowsCollection) {
        if (movedFromIndex === movedToIndex) {
            return;
        }

        rowsCollection = rowsCollection || this._getRows(true);

        const wasMovedUp = movedToIndex < movedFromIndex;
        const iMin = Math.min(movedToIndex, movedFromIndex);
        const iMax = Math.max(movedToIndex, movedFromIndex);

        let row, pval;

        if (wasMovedUp) {
            pval = this._getRowValue(rowsCollection[iMin]);

            for (let i = iMax; i >= iMin; --i) {
                row = rowsCollection[i];
                row.toggleAttribute('data-changed', true);
                pval = this._setRowValue(row, pval);
            }
        }
        else {
            pval = this._getRowValue(rowsCollection[iMax]);

            for (let i = iMin; i <= iMax; ++i) {
                row = rowsCollection[i];
                row.toggleAttribute('data-changed', true);
                pval = this._setRowValue(row, pval);
            }
        }
    }

    _moveSelectedRows(fnMoveSelectedRows) {
        if (!this._isSelecting) { return; }

        const allRows = this._getRows(true);
        const selectedRows = [];

        for (const row of allRows) {
            if (row.classList.contains('selected')) {
                selectedRows.push(row);
            }
            else if (selectedRows.length > 0) {
                // We've traversed over a number of selected rows (and added them into selectedRows)
                // and now we've reached a gap in the selection, so process this block of selected rows.
                fnMoveSelectedRows.call(this, allRows, selectedRows);

                // Empty array of rows we've processed.
                selectedRows.splice(0, selectedRows.length);
            }
            // else nothing in selectedRows yet, so nothing to do, continue looping...
        }

        // Process the last batch, if there is one.
        if (selectedRows.length > 0) {
            fnMoveSelectedRows.call(this, allRows, selectedRows);
        }
    }
    _moveSelectedRows_up(allRows, selectedRows) {
        const precedingIndex = this._getRowIndex(this._firstRowOf(selectedRows), allRows) - 1;
        // Can't move any more upwards, abort.
        if (precedingIndex < 0) { return; }

        const precedingRow = allRows[precedingIndex];

        // Move the row preceding the selected ones so it's after the selected rows
        // effectively moving the selected rows up one spot.
        this._lastRowOf(selectedRows).after(precedingRow);

        // Reorder row values for the changed rows, including selected rows and the moved row.
        this._resequenceRowRangeValues(precedingIndex, precedingIndex + selectedRows.length);
    }
    _moveSelectedRows_down(allRows, selectedRows) {
        const followingIndex = this._getRowIndex(this._lastRowOf(selectedRows), allRows) + 1;
        // Can't move any more downwards, abort.
        if (followingIndex >= allRows.length) { return; }

        const followingRow = allRows[followingIndex];

        // Move the row following the selected one so it's before the selected rows
        // effectively moving the selected rows down one spot.
        this._firstRowOf(selectedRows).before(followingRow);

        // Reorder row values for the changed rows, including selected rows and the moved row.
        this._resequenceRowRangeValues(followingIndex, followingIndex - selectedRows.length);
    }

});